import { useRef } from "react";
import type { PieVoucher } from "@/lib/api";

interface Props {
  voucher: PieVoucher;
  onClose: () => void;
}

export default function VoucherPieModal({ voucher, onClose }: Props) {
  const printRef = useRef<HTMLDivElement>(null);

  function handlePrint() {
    const content = printRef.current?.innerHTML ?? "";
    const win = window.open("", "_blank", "width=400,height=600");
    if (!win) return;
    win.document.write(`
      <html><head><title>Voucher Pie Repactación</title>
      <style>
        body { font-family: monospace; font-size: 12px; margin: 16px; }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        hr { border: none; border-top: 1px dashed #000; margin: 8px 0; }
        .row { display: flex; justify-content: space-between; margin: 2px 0; }
        .title { font-size: 14px; font-weight: bold; text-align: center; margin: 6px 0; }
        .total { font-size: 16px; font-weight: bold; text-align: center; margin: 8px 0; }
      </style></head>
      <body>${content}</body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col">

        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">Voucher de pago — Pie repactación</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Recibo */}
        <div className="px-5 py-4 overflow-y-auto max-h-[70vh]">
          <div ref={printRef} className="font-mono text-xs space-y-1">
            <div className="title">APR QUECHUREHUE</div>
            <div className="center">COMPROBANTE DE PAGO</div>
            <div className="center">REPACTACIÓN</div>
            <hr />
            <div className="row"><span>Folio:</span><span className="bold">{voucher.folio}</span></div>
            <div className="row"><span>Fecha:</span><span>{voucher.fecha}</span></div>
            <div className="row"><span>Hora:</span><span>{voucher.hora}</span></div>
            <div className="row"><span>Cajero:</span><span>{voucher.cajero}</span></div>
            <hr />
            <div className="row"><span>Cliente:</span><span className="bold">{voucher.cliente}</span></div>
            <div className="row"><span>RUT:</span><span>{voucher.rut}</span></div>
            <div className="row"><span>Contrato:</span><span>{voucher.contrato}</span></div>
            <hr />
            <div className="row"><span>Deuda total repactada:</span><span>${voucher.montoTotal.toLocaleString("es-CL")}</span></div>
            <div className="row"><span>N° cuotas:</span><span>{voucher.cuotas}</span></div>
            <div className="row"><span>Valor cuota:</span><span>${voucher.montoCuota.toLocaleString("es-CL")}</span></div>
            <div className="row"><span>Período inicio:</span><span>{voucher.periodoInicio}</span></div>
            {voucher.periodoTermino !== "-" && (
              <div className="row"><span>Período término:</span><span>{voucher.periodoTermino}</span></div>
            )}
            <hr />
            <div className="center bold">PIE CANCELADO EN CAJA</div>
            <div className="total">${voucher.montoPie.toLocaleString("es-CL")}</div>
            <hr />
            <div className="center" style={{ fontSize: "10px" }}>
              Ref: {voucher.agreementId.slice(-12).toUpperCase()}
            </div>
            <div className="center" style={{ fontSize: "10px" }}>
              Las cuotas se cobrarán mensualmente en su boleta de agua.
            </div>
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 text-sm text-gray-500 hover:text-gray-700 py-2"
          >
            Cerrar
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
          >
            <i className="ri-printer-line" /> Imprimir
          </button>
        </div>
      </div>
    </div>
  );
}
